import { useState } from "react";
import { SlideFrame, SlideHeader, Reveal } from "@/components/presentation/slides/SlideFrame";
import { MediaPlaceholder } from "@/components/primitives/MediaPlaceholder";
import { cn } from "@/lib/cn";

/**
 * 05 · ProductWalkthroughSlide
 *
 * A device-style frame holding a small carousel of placeholder screens
 * (one shown at a time, with quiet dots to switch). Supporting copy lives
 * to the side so the visual remains the protagonist.
 *
 * The "device" is intentionally generic — a rounded outer frame with thin
 * border, no platform-specific chrome — so the same component is reused for
 * web, mobile, or kiosk surfaces.
 */
export const ProductWalkthroughSlide = ({
  eyebrow = "Product",
  title = "How it feels in the hand.",
  narrative = "One sentence about the experience that supports — but never competes with — the visual.",
  screens = [
    { id: "s1", label: "Screen one" },
    { id: "s2", label: "Screen two" },
    { id: "s3", label: "Screen three" },
  ],
}) => {
  const [index, setIndex] = useState(0);
  const active = screens[index] || screens[0];

  return (
    <SlideFrame testId="slide-product-walkthrough">
      <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
        <div className="flex flex-col gap-6 lg:col-span-4">
          <SlideHeader eyebrow={eyebrow} title={title} lede={narrative} />
        </div>
        <Reveal delay={2} className="lg:col-span-8">
          <figure
            data-testid="walkthrough-device"
            className="relative mx-auto w-full max-w-2xl rounded-3xl border border-border bg-surface/40 p-3 shadow-sm"
          >
            <div className="overflow-hidden rounded-2xl border border-border bg-background">
              <MediaPlaceholder
                kind="image"
                label={active.label}
                ratio="16/10"
                testId={`walkthrough-screen-${active.id}`}
                className="rounded-none border-0"
              />
            </div>
            <figcaption className="mt-4 flex items-center justify-center gap-2" data-testid="walkthrough-dots">
              {screens.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setIndex(i)}
                  data-testid={`walkthrough-dot-${i}`}
                  aria-label={`Show ${s.label}`}
                  aria-current={i === index ? "true" : undefined}
                  className={cn(
                    "h-1.5 rounded-full transition-all duration-300 ease-out-expo focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                    i === index ? "w-6 bg-foreground" : "w-1.5 bg-border hover:bg-foreground/40"
                  )}
                />
              ))}
            </figcaption>
          </figure>
        </Reveal>
      </div>
    </SlideFrame>
  );
};

export default ProductWalkthroughSlide;
